import { isUsableAccent, ratioToOneDecimal } from '@/theme/contrast';
import { colors, whiteLabel } from '@/theme/tokens';

/**
 * §6.10's accent field, as two functions — so a test can drive the rule the field applies without
 * rendering the section, and so the section stays a component module (the fast-refresh rule).
 *
 * ⚠️ **The shape is the server's shape**, including the optional `#`: `apps.core.contrast._HEX` accepts
 * `#rgb`, `#rrggbb` and both without the hash, and it accepts the bare form **because this client
 * already did** — the shared fixture table caught the two disagreeing about `C2410C` on its first run,
 * and the server was widened rather than this narrowed (somebody pasting a hex out of a design document
 * is right). A stricter regex here would put the divergence back.
 */
export const isHex = (value: string): boolean =>
  /^#?(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());

/**
 * §2's rule, measured against the page the accent will be read on and the foreground a filled button
 * carries — the same two colours the server measures against, and both are in the shared fixture table
 * so the two implementations cannot disagree about *what* they measure either.
 */
export const accentIsUsable = (value: string): boolean =>
  isHex(value) && isUsableAccent(value.trim(), whiteLabel.accentForeground, colors.paper.bg);

/**
 * The measured ratio a refusal shows, so *"choose another colour"* is never the whole message.
 *
 * ⚠️ **Rounded, and called by the field** (BUG-040). It used to return the raw
 * `contrastRatio(value.trim(), colors.paper.bg)` and had **no production caller**: `BrandingSection`
 * composed the same two arguments inline against `ratioToOneDecimal` instead, so the one thing this
 * module exists for — *the rule the field applies, drivable without rendering the section* — was
 * duplicated, and `settings.test.tsx`'s *"refuses an unreadable accent BEFORE the request, with the
 * measured ratio"* exercised a function the field never called. One definition now, rounded the way the
 * sentence renders it, so the test and the screen cannot disagree about the number.
 */
export const accentRatio = (value: string): number =>
  ratioToOneDecimal(value.trim(), colors.paper.bg);
